import React from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '../../notifications';
import {
  getAssistantVoiceSettings,
  resetAssistantVoiceSettings,
  updateAssistantVoiceSettings,
} from '../services/assistantVoiceSettingsService';
import type { AssistantVoiceSettings } from '../types/Company';
import styles from './AssistantVoiceSettingsPanel.module.css';

const SPEECH_MODELS = ['gpt-4o-mini-tts', 'gpt-4o-tts'];

const emptyForm = (): AssistantVoiceSettings => ({
  enabled: true,
  mode: 'Duplex',
  voice: 'alloy',
  speechModel: 'gpt-4o-mini-tts',
  streamSpeech: true,
  bargeInEnabled: true,
  allowInternalAiDirectOnly: true,
  endpointingSilenceMs: 650,
  minUtteranceMs: 280,
  maxUtteranceSeconds: 20,
  vadRmsThreshold: 0.015,
  bargeInRmsThreshold: 0.045,
  bargeInHoldoffMs: 400,
  minTranscriptChars: 2,
  speed: 1,
  instructions: '',
  realtimeEnabled: true,
  realtimeToolsEnabled: true,
  maxVoiceTokens: 0,
  voiceTokensUsed: 0,
  voiceTokensRemaining: null,
  isOverridden: false,
  availableVoices: ['alloy', 'ash', 'coral', 'echo', 'fable', 'nova', 'onyx', 'sage', 'shimmer', 'verse'],
  availableModes: ['Duplex', 'PushToTalk'],
});

const formatVoiceLabel = (voice: string) =>
  voice ? `${voice.charAt(0).toUpperCase()}${voice.slice(1)}` : voice;

export const AssistantVoiceSettingsPanel: React.FC = () => {
  const { t } = useTranslation('companies');
  const { showToast } = useToast();
  const fieldId = React.useId();
  const [form, setForm] = React.useState<AssistantVoiceSettings>(emptyForm);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setForm(await getAssistantVoiceSettings());
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('failedToLoadAssistantVoiceSettings'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await updateAssistantVoiceSettings({
        enabled: form.enabled,
        mode: form.mode,
        voice: form.voice,
        speechModel: form.speechModel,
        streamSpeech: form.streamSpeech,
        bargeInEnabled: form.bargeInEnabled,
        allowInternalAiDirectOnly: form.allowInternalAiDirectOnly,
        endpointingSilenceMs: form.endpointingSilenceMs,
        minUtteranceMs: form.minUtteranceMs,
        maxUtteranceSeconds: form.maxUtteranceSeconds,
        vadRmsThreshold: form.vadRmsThreshold,
        bargeInRmsThreshold: form.bargeInRmsThreshold,
        bargeInHoldoffMs: form.bargeInHoldoffMs,
        minTranscriptChars: form.minTranscriptChars,
        speed: form.speed,
        instructions: form.instructions,
        realtimeEnabled: form.realtimeEnabled,
        realtimeToolsEnabled: form.realtimeToolsEnabled,
        maxVoiceTokens: form.maxVoiceTokens,
      });
      setForm(updated);
      showToast(t('assistantVoiceSettingsSaved'), 'success');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('failedToSaveAssistantVoiceSettings'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setResetting(true);
    setError(null);
    try {
      const updated = await resetAssistantVoiceSettings();
      setForm(updated);
      showToast(t('assistantVoiceSettingsReset'), 'success');
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : t('failedToResetAssistantVoiceSettings'));
    } finally {
      setResetting(false);
    }
  };

  const voiceOptions = form.availableVoices.includes(form.voice)
    ? form.availableVoices
    : [...form.availableVoices, form.voice];

  const live = form.enabled;

  return (
    <section className={`${styles.section} ${live ? styles.sectionLive : ''}`}>
      <div className={styles.header}>
        <div className={styles.iconWrap} aria-hidden="true">
          <span className={`${styles.waveform} ${live ? styles.waveformLive : ''}`}>
            <span className={styles.bar} />
            <span className={styles.bar} />
            <span className={styles.bar} />
            <span className={styles.bar} />
            <span className={styles.bar} />
          </span>
        </div>
        <div className={styles.headerCopy}>
          <div className={styles.titleRow}>
            <h4 className={styles.title}>{t('assistantVoiceSettingsHeading')}</h4>
            <span
              className={`${styles.statusChip} ${form.isOverridden ? styles.statusChipCustom : ''}`}
              title={form.isOverridden
                ? t('assistantVoiceSettingsCustomOverride')
                : t('assistantVoiceSettingsUsingDefaults')}
            >
              {form.isOverridden ? t('assistantVoiceStatusCustom') : t('assistantVoiceStatusDefaults')}
            </span>
          </div>
          <p className={styles.description}>{t('assistantVoiceSettingsDescription')}</p>
        </div>
      </div>

      {error ? <div className="error">{error}</div> : null}

      {loading ? (
        <p className={styles.loading}>{t('loadingAssistantVoiceSettings')}</p>
      ) : (
        <>
          <div className={`${styles.enableRow} ${live ? styles.enableRowLive : ''}`}>
            <div className={styles.enableCopy}>
              <span className={styles.enableTitle} id={`${fieldId}-enabled`}>
                {t('assistantVoiceEnabled')}
              </span>
              <span className={styles.enableHint}>{t('assistantVoiceEnabledHelp')}</span>
            </div>
            <SwitchControl
              checked={form.enabled}
              labelledBy={`${fieldId}-enabled`}
              onChange={enabled => setForm(current => ({ ...current, enabled }))}
            />
          </div>

          <div className={styles.card}>
            <h5 className={styles.cardTitle}>{t('assistantVoiceCallExperience')}</h5>
            <div className={styles.fields}>
              <div className={styles.field}>
                <span className={styles.fieldLabel} id={`${fieldId}-mode`}>
                  {t('assistantVoiceMode')}
                </span>
                <div className={styles.segmented} role="group" aria-labelledby={`${fieldId}-mode`}>
                  <button
                    type="button"
                    className={`${styles.segment} ${form.mode === 'Duplex' ? styles.segmentActive : ''}`}
                    aria-pressed={form.mode === 'Duplex'}
                    onClick={() => setForm(current => ({ ...current, mode: 'Duplex' }))}
                  >
                    {t('assistantVoiceModeDuplexShort')}
                    <span className={styles.recommended}>{t('assistantVoiceRecommended')}</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.segment} ${form.mode === 'PushToTalk' ? styles.segmentActive : ''}`}
                    aria-pressed={form.mode === 'PushToTalk'}
                    onClick={() => setForm(current => ({ ...current, mode: 'PushToTalk' }))}
                  >
                    {t('assistantVoiceModePushToTalk')}
                  </button>
                </div>
              </div>

              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('assistantVoiceVoice')}</span>
                <select
                  className={`text-input ${styles.control}`}
                  value={form.voice}
                  onChange={event => setForm(current => ({ ...current, voice: event.target.value }))}
                >
                  {voiceOptions.map(voice => (
                    <option key={voice} value={voice}>
                      {formatVoiceLabel(voice)}
                    </option>
                  ))}
                </select>
              </label>

              <label className={`${styles.field} ${styles.fieldFull}`}>
                <span className={styles.fieldLabel}>{t('assistantVoiceInstructions')}</span>
                <textarea
                  className={`text-input ${styles.textarea}`}
                  rows={3}
                  maxLength={1000}
                  value={form.instructions}
                  onChange={event => setForm(current => ({ ...current, instructions: event.target.value }))}
                />
                <span className={styles.help}>{t('assistantVoiceInstructionsHelp')}</span>
              </label>
            </div>
          </div>

          <div className={styles.card}>
            <h5 className={styles.cardTitle}>{t('assistantVoiceConversationBehavior')}</h5>
            <div className={styles.toggleList}>
              <ToggleRow
                titleId={`${fieldId}-barge-in`}
                title={t('assistantVoiceBargeIn')}
                hint={t('assistantVoiceBargeInHelp')}
                checked={form.bargeInEnabled}
                onChange={bargeInEnabled => setForm(current => ({ ...current, bargeInEnabled }))}
              />
              <ToggleRow
                titleId={`${fieldId}-stream`}
                title={t('assistantVoiceStreamSpeech')}
                hint={t('assistantVoiceStreamSpeechHelp')}
                checked={form.streamSpeech}
                onChange={streamSpeech => setForm(current => ({ ...current, streamSpeech }))}
              />
              <ToggleRow
                titleId={`${fieldId}-internal`}
                title={t('assistantVoiceInternalOnly')}
                hint={t('assistantVoiceInternalOnlyHelp')}
                checked={form.allowInternalAiDirectOnly}
                onChange={allowInternalAiDirectOnly => setForm(current => ({
                  ...current,
                  allowInternalAiDirectOnly,
                }))}
              />
              <ToggleRow
                titleId={`${fieldId}-realtime`}
                title={t('assistantVoiceRealtime')}
                hint={t('assistantVoiceRealtimeHelp')}
                checked={form.realtimeEnabled}
                onChange={realtimeEnabled => setForm(current => ({ ...current, realtimeEnabled }))}
              />
              <ToggleRow
                titleId={`${fieldId}-realtime-tools`}
                title={t('assistantVoiceRealtimeTools')}
                hint={t('assistantVoiceRealtimeToolsHelp')}
                checked={form.realtimeToolsEnabled}
                onChange={realtimeToolsEnabled => setForm(current => ({ ...current, realtimeToolsEnabled }))}
              />
              <label className={styles.field}>
                <span className={styles.fieldLabel}>{t('assistantVoiceTokenLimit')}</span>
                <input
                  className={`text-input ${styles.control}`}
                  type="number"
                  min={0}
                  max={1000000000}
                  placeholder={t('assistantVoiceTokenLimitUnlimited')}
                  value={form.maxVoiceTokens > 0 ? form.maxVoiceTokens : ''}
                  onChange={event => {
                    const raw = event.target.value.trim();
                    setForm(current => ({
                      ...current,
                      maxVoiceTokens: raw === '' ? 0 : Math.max(0, Math.floor(Number(event.target.value) || 0)),
                    }));
                  }}
                />
                <span className={styles.help}>
                  {form.maxVoiceTokens > 0
                    ? t('assistantVoiceTokenLimitUsage', {
                      used: form.voiceTokensUsed.toLocaleString(),
                      limit: form.maxVoiceTokens.toLocaleString(),
                      remaining: Math.max(0, form.maxVoiceTokens - form.voiceTokensUsed).toLocaleString(),
                    })
                    : t('assistantVoiceTokenLimitHelp')}
                </span>
              </label>
            </div>
          </div>

          <button
            type="button"
            className={styles.advancedToggle}
            onClick={() => setShowAdvanced(current => !current)}
          >
            {showAdvanced ? t('assistantVoiceHideAdvanced') : t('assistantVoiceShowAdvanced')}
          </button>

          {showAdvanced ? (
            <div className={styles.card}>
              <div className={styles.advancedGrid}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('assistantVoiceSpeechModel')}</span>
                  <select
                    className={`text-input ${styles.control}`}
                    value={form.speechModel}
                    onChange={event => setForm(current => ({ ...current, speechModel: event.target.value }))}
                  >
                    {SPEECH_MODELS.includes(form.speechModel)
                      ? null
                      : <option value={form.speechModel}>{form.speechModel}</option>}
                    {SPEECH_MODELS.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </select>
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('assistantVoiceSilenceMs')}</span>
                  <input
                    className={`text-input ${styles.control}`}
                    type="number"
                    min={200}
                    max={2500}
                    value={form.endpointingSilenceMs}
                    onChange={event => setForm(current => ({
                      ...current,
                      endpointingSilenceMs: Number(event.target.value),
                    }))}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('assistantVoiceMinUtteranceMs')}</span>
                  <input
                    className={`text-input ${styles.control}`}
                    type="number"
                    min={80}
                    max={1500}
                    value={form.minUtteranceMs}
                    onChange={event => setForm(current => ({
                      ...current,
                      minUtteranceMs: Number(event.target.value),
                    }))}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('assistantVoiceMaxUtteranceSeconds')}</span>
                  <input
                    className={`text-input ${styles.control}`}
                    type="number"
                    min={3}
                    max={60}
                    value={form.maxUtteranceSeconds}
                    onChange={event => setForm(current => ({
                      ...current,
                      maxUtteranceSeconds: Number(event.target.value),
                    }))}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('assistantVoiceVadThreshold')}</span>
                  <input
                    className={`text-input ${styles.control}`}
                    type="number"
                    min={0.001}
                    max={1}
                    step={0.001}
                    value={form.vadRmsThreshold}
                    onChange={event => setForm(current => ({
                      ...current,
                      vadRmsThreshold: Number(event.target.value),
                    }))}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('assistantVoiceBargeInThreshold')}</span>
                  <input
                    className={`text-input ${styles.control}`}
                    type="number"
                    min={0.001}
                    max={1}
                    step={0.001}
                    value={form.bargeInRmsThreshold}
                    onChange={event => setForm(current => ({
                      ...current,
                      bargeInRmsThreshold: Number(event.target.value),
                    }))}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('assistantVoiceBargeInHoldoffMs')}</span>
                  <input
                    className={`text-input ${styles.control}`}
                    type="number"
                    min={0}
                    max={2500}
                    value={form.bargeInHoldoffMs}
                    onChange={event => setForm(current => ({
                      ...current,
                      bargeInHoldoffMs: Number(event.target.value),
                    }))}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>{t('assistantVoiceSpeed')}</span>
                  <input
                    className={`text-input ${styles.control}`}
                    type="number"
                    min={0.25}
                    max={4}
                    step={0.05}
                    value={form.speed}
                    onChange={event => setForm(current => ({
                      ...current,
                      speed: Number(event.target.value),
                    }))}
                  />
                </label>
              </div>
            </div>
          ) : null}

          <div className={styles.footer}>
            <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={saving || resetting}>
              {saving ? t('savingEllipsis', { ns: 'admin' }) : t('saveAssistantVoiceSettings')}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void reset()}
              disabled={saving || resetting || !form.isOverridden}
            >
              {resetting ? t('savingEllipsis', { ns: 'admin' }) : t('resetAssistantVoiceSettings')}
            </button>
          </div>
        </>
      )}
    </section>
  );
};

const SwitchControl: React.FC<{
  checked: boolean;
  onChange: (checked: boolean) => void;
  labelledBy: string;
}> = ({ checked, onChange, labelledBy }) => (
  <label className={styles.switch}>
    <input
      type="checkbox"
      className={styles.switchInput}
      checked={checked}
      aria-labelledby={labelledBy}
      onChange={event => onChange(event.target.checked)}
    />
    <span className={styles.switchSlider} aria-hidden="true" />
  </label>
);

const ToggleRow: React.FC<{
  titleId: string;
  title: string;
  hint: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}> = ({ titleId, title, hint, checked, onChange }) => (
  <div className={styles.toggleRow}>
    <div className={styles.toggleCopy}>
      <span className={styles.toggleTitle} id={titleId}>{title}</span>
      <span className={styles.toggleHint}>{hint}</span>
    </div>
    <SwitchControl checked={checked} labelledBy={titleId} onChange={onChange} />
  </div>
);
